import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Icon } from './Icon';
import type { IconName } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and keeps its width. */
  loading?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
  /** Stretches to the container. The default on phones for primary actions. */
  block?: boolean;
  children?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-on-accent border-transparent hover:bg-accent-hover active:translate-y-px shadow-card',
  secondary: 'bg-surface text-ink border-line-strong hover:bg-raised hover:border-ink/25',
  ghost: 'bg-transparent text-muted border-transparent hover:bg-sunken hover:text-ink',
  danger: 'bg-danger-soft text-danger-ink border-danger/30 hover:bg-danger hover:text-on-accent',
};

const SIZES: Record<ButtonSize, string> = {
  // 36px, 40px, 44px. The medium is the smallest comfortable tap target and is
  // what phones get; `lg` is for the one primary action on a form.
  sm: 'h-9 px-3 text-small',
  md: 'h-10 px-4 text-ui',
  lg: 'h-11 px-5 text-body',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  block = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Buttons inside forms default to `submit`, which submits by accident.
      // Opt in explicitly instead.
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex select-none items-center justify-center rounded-md border font-medium',
        'transition-[background-color,border-color,color,transform] duration-150',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          className="absolute size-4 animate-[spin_0.7s_linear_infinite] rounded-full border-2 border-current/30 border-t-current"
          aria-hidden="true"
        />
      ) : null}
      <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>
        {iconLeft ? <Icon name={iconLeft} size={size === 'sm' ? 14 : 16} /> : null}
        {children}
        {iconRight ? <Icon name={iconRight} size={size === 'sm' ? 14 : 16} /> : null}
      </span>
    </button>
  );
}
